/* eslint-disable no-unused-vars */
import React, { useMemo } from 'react';
import { NM, COST_FIELDS, PRE_MONTHS } from '../utils/constants';
import { mlSafe, fu } from '../utils/format';
import { getCostState, getDealTerms } from '../utils/stateFactory';
import { calcRoyalties, calcPlatform } from '../utils/calc';

const AMBER  = '#f5a623';
const GREEN  = 'var(--green)';
const RED    = '#ff4d4d';
const BLUE   = '#3d8bff';
const PURPLE = '#a78bfa';
const TEAL   = '#00c4b4';

// Chart palette — monochromatic warm ramp for outflows
const C_INFLOW   = '#4db8e8';  // sky blue
const C_DEV      = '#f5a623';  // amber
const C_ROY      = '#d4853a';  // mid amber-brown
const C_PUB      = '#a05a1a';  // deeper brown
const C_PAYROLL  = '#6b3a08';  // dark brown (darkest outflow)
const C_CASHLINE = '#00d98b';  // bright green cash line

export default function CashOverview({ cashflowData, currentMonthIdx, startDate, functionalCurrency, games, CS, DT, PS }) {
  const fc  = functionalCurrency || 'USD';
  const cf  = cashflowData || {};
  const now = currentMonthIdx ?? 0;

  const windowEnd   = Math.min(now + 11, NM - 1);
  const windowMonths = Array.from({ length: windowEnd - now + 1 }, (_, i) => now + i);

  // ── KEY METRICS
  const metrics = useMemo(() => {
    const closingNow = cf.close?.[now] ?? 0;
    let totalIn = 0, totalOut = 0;
    windowMonths.forEach(i => {
      totalIn  += cf.receipts?.[i] || 0;
      totalOut += (cf.devOut?.[i] || 0) + (cf.pubOut?.[i] || 0) +
                  (cf.royOut?.[i] || 0) + (cf.payrollOut?.[i] || 0);
    });
    let lowestVal = Infinity, lowestMonth = null;
    windowMonths.forEach(i => {
      const v = cf.close?.[i] ?? 0;
      if (v < lowestVal) { lowestVal = v; lowestMonth = i; }
    });
    let runOutMonth = null;
    for (let i = now; i < NM; i++) {
      if ((cf.close?.[i] ?? 0) < 0) { runOutMonth = i; break; }
    }
    const endCash = cf.close?.[windowEnd] ?? 0;
    return { closingNow, totalIn, totalOut, lowestVal, lowestMonth, runOutMonth, endCash };
  }, [cf, now, windowMonths, windowEnd]);

  // ── CHART DATA — stacked outflows + inflow + closing cash line
  const chartData = useMemo(() => windowMonths.map(i => ({
    month:     mlSafe(i, startDate),
    inflow:    cf.receipts?.[i]   || 0,
    devOut:    cf.devOut?.[i]     || 0,
    pubOut:    cf.pubOut?.[i]     || 0,
    royOut:    cf.royOut?.[i]     || 0,
    payroll:   cf.payrollOut?.[i] || 0,
    close:     cf.close?.[i]      ?? 0,
  })), [windowMonths, cf, startDate]);

  const maxInflow   = Math.max(...chartData.map(d => d.inflow), 1);
  const maxOutflow  = Math.max(...chartData.map(d => d.devOut + d.pubOut + d.royOut + d.payroll), 1);
  const maxAbsClose = Math.max(...chartData.map(d => Math.abs(d.close)), 1);
  const chartMax    = Math.max(maxInflow, maxOutflow, 1);

  const W = 720, H = 160, PAD_L = 58, PAD_B = 32, PAD_T = 10, PAD_R = 60;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_B - PAD_T;
  const midY   = PAD_T + chartH / 2; // zero line at middle
  const barGroupW = chartW / chartData.length;
  const barW = Math.max(3, barGroupW * 0.55);

  function scalePos(v) { return (v / chartMax) * (chartH / 2); }
  function scaleClose(v) { return (v / maxAbsClose) * (chartH / 2); }

  function fAxis(n) {
    if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
    if (Math.abs(n) >= 1_000)     return Math.round(n / 1_000) + 'K';
    return Math.round(n).toString();
  }

  // ── UPCOMING PAYMENTS
  const upcomingPayments = useMemo(() => {
    const items = [];
    const next3 = [now, now + 1, now + 2].filter(i => i < NM);

    games.forEach(game => {
      const costs = getCostState(CS, game);
      next3.forEach(mi => {
        let devTotal = 0;
        ['recoup','nonRecoup'].forEach(sec => {
          costs[sec].devAdvances.forEach((v, ai) => {
            if (ai - PRE_MONTHS === mi) devTotal += parseFloat(v) || 0;
          });
        });
        if (devTotal > 0) items.push({ game, type: 'Dev Advance', month: mi, amount: devTotal });

        COST_FIELDS.filter(f => f.key !== 'devAdvances').forEach(({ key, label }) => {
          let total = 0;
          ['recoup','nonRecoup'].forEach(sec => {
            (costs[sec][key] || []).forEach((v, ai) => {
              if (ai - PRE_MONTHS === mi) total += parseFloat(v) || 0;
            });
          });
          if (total > 0) items.push({ game, type: label, month: mi, amount: total });
        });
      });
    });

    next3.forEach(mi => {
      const total = cf.royOut?.[mi] || 0;
      if (total > 0) items.push({ game: 'All Games', type: 'Royalties', month: mi, amount: total });
    });

    items.sort((a, b) => b.amount - a.amount);
    const top10 = items.slice(0, 10);
    const otherTotal = items.slice(10).reduce((a, v) => a + v.amount, 0);
    return { top10, otherTotal };
  }, [games, CS, DT, cf, now]);

  const STACKS = [
    { key: 'devOut',  label: 'Dev Advances',    color: C_DEV },
    { key: 'royOut',  label: 'Royalties',        color: C_ROY },
    { key: 'pubOut',  label: 'Direct Costs',     color: C_PUB },
    { key: 'payroll', label: 'Overheads',         color: C_PAYROLL },
  ];

  return (
    <div>
      {/* METRICS */}
      <div className="out-summary-bar" style={{ marginBottom: 16 }}>
        {[
          {
            label: 'Current Cash Balance',
            value: fu(metrics.closingNow),
            color: metrics.closingNow >= 0 ? GREEN : RED,
            sub: currentMonthIdx !== null ? mlSafe(now, startDate) : 'No month-end set',
          },
          { label: 'Cash In (Next 12 Months)',  value: fu(metrics.totalIn),  color: BLUE },
          { label: 'Cash Out (Next 12 Months)', value: fu(metrics.totalOut), color: AMBER },
          {
            label: 'Lowest Cash Point',
            value: fu(metrics.lowestVal),
            color: metrics.lowestVal < 0 ? RED : GREEN,
            sub: metrics.lowestMonth !== null ? mlSafe(metrics.lowestMonth, startDate) : '—',
          },
          {
            label: metrics.runOutMonth !== null ? 'Cash Runs Out' : 'Cash Positive Throughout',
            value: metrics.runOutMonth !== null ? mlSafe(metrics.runOutMonth, startDate) : 'YES',
            color: metrics.runOutMonth !== null ? RED : GREEN,
          },
          {
            label: '12-Month Closing Cash',
            value: fu(metrics.endCash),
            color: metrics.endCash >= 0 ? GREEN : RED,
            sub: mlSafe(windowEnd, startDate),
          },
        ].map((m, i) => (
          <div key={i} className="out-metric">
            <div className="out-metric-label">{m.label}</div>
            <div className="out-metric-value" style={{ color: m.color, fontSize: 16 }}>{m.value}</div>
            {m.sub && <div style={{ fontSize: 10, color: 'var(--t3)', marginTop: 2 }}>{m.sub}</div>}
          </div>
        ))}
      </div>

      {/* CHART */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head">
          <span className="card-title">Cash Flow — Next 12 Months ({fc})</span>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
            <span style={{ fontSize: 9, color: C_INFLOW }}>■ Inflow</span>
            {STACKS.map(s => <span key={s.key} style={{ fontSize: 9, color: s.color }}>■ {s.label}</span>)}
            <span style={{ fontSize: 9, color: C_CASHLINE }}>— Cash Balance</span>
          </div>
        </div>
        <div className="card-body">
          <svg width="100%" viewBox={`0 0 ${W} ${H}`}>
            {/* Y-axis grid lines — positive side */}
            {[0.25, 0.5, 0.75, 1].map((f, i) => {
              const yPos = midY - scalePos(chartMax * f);
              const yNeg = midY + scalePos(chartMax * f);
              return (
                <g key={i}>
                  <line x1={PAD_L} y1={yPos} x2={W - PAD_R} y2={yPos} stroke="var(--e0)" strokeWidth="0.5" strokeDasharray="3,3" />
                  <line x1={PAD_L} y1={yNeg} x2={W - PAD_R} y2={yNeg} stroke="var(--e0)" strokeWidth="0.5" strokeDasharray="3,3" />
                  <text x={PAD_L - 4} y={yPos + 3} textAnchor="end" fill="var(--t4)" fontSize="6">{fAxis(chartMax * f)}</text>
                  <text x={PAD_L - 4} y={yNeg + 3} textAnchor="end" fill="var(--t4)" fontSize="6">-{fAxis(chartMax * f)}</text>
                </g>
              );
            })}
            {/* Zero line */}
            <line x1={PAD_L} y1={midY} x2={W - PAD_R} y2={midY} stroke="var(--e2)" strokeWidth="1" />
            <text x={PAD_L - 4} y={midY + 3} textAnchor="end" fill="var(--t3)" fontSize="6">0</text>

            {/* Bars */}
            {chartData.map((d, idx) => {
              const cx = PAD_L + idx * barGroupW + barGroupW / 2;
              const inflowH = scalePos(d.inflow);

              // Stacked negative bars
              let stackY = midY;
              const stackBars = STACKS.map(s => {
                const h = scalePos(d[s.key]);
                const bar = { x: cx - barW / 2, y: stackY, h, color: s.color, label: `${s.label}: ${fu(d[s.key])}` };
                stackY += h;
                return bar;
              });

              return (
                <g key={idx}>
                  {/* Inflow bar (positive) */}
                  <rect x={cx - barW / 2} y={midY - inflowH} width={barW} height={inflowH}
                    fill={C_INFLOW} opacity="0.88">
                    <title>Inflow: {fu(d.inflow)}</title>
                  </rect>
                  {/* Stacked outflow bars (negative) */}
                  {stackBars.filter(b => b.h > 0).map((b, bi) => (
                    <rect key={bi} x={b.x} y={b.y} width={barW} height={b.h}
                      fill={b.color} opacity="0.85">
                      <title>{b.label}</title>
                    </rect>
                  ))}
                  {/* X labels */}
                  <text x={cx} y={H - PAD_B + 12} textAnchor="middle"
                    fill="var(--t4)" fontSize="6" fontFamily="var(--sans)">
                    {d.month}
                  </text>
                </g>
              );
            })}

            {/* Cash balance line (right axis) */}
            {chartData.length > 1 && (() => {
              const points = chartData.map((d, idx) => {
                const cx = PAD_L + idx * barGroupW + barGroupW / 2;
                const cy = midY - scaleClose(d.close);
                return `${cx},${cy}`;
              }).join(' ');
              return (
                <>
                  <polyline points={points} fill="none" stroke={C_CASHLINE} strokeWidth="2" strokeDasharray="none" />
                  {chartData.map((d, idx) => {
                    const cx = PAD_L + idx * barGroupW + barGroupW / 2;
                    const cy = midY - scaleClose(d.close);
                    return (
                      <circle key={idx} cx={cx} cy={cy} r="2.5"
                        fill={d.close >= 0 ? C_CASHLINE : RED}>
                        <title>Cash: {fu(d.close)}</title>
                      </circle>
                    );
                  })}
                </>
              );
            })()}

            {/* Right axis label */}
            <text x={W - PAD_R + 4} y={midY - scaleClose(maxAbsClose) + 4}
              fill={GREEN} fontSize="6">+{fAxis(maxAbsClose)}</text>
            <text x={W - PAD_R + 4} y={midY + scaleClose(maxAbsClose) + 4}
              fill={RED} fontSize="6">-{fAxis(maxAbsClose)}</text>
          </svg>
        </div>
      </div>

      {/* UPCOMING PAYMENTS */}
      <div className="card">
        <div className="card-head">
          <span className="card-title">Upcoming Payments — Next 3 Months</span>
          <span className="card-meta">Top 10 outgoings · Game costs only</span>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <table className="admin-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Game</th>
                <th style={{ textAlign: 'left' }}>Type</th>
                <th style={{ textAlign: 'left' }}>Month</th>
                <th style={{ textAlign: 'right' }}>Amount ({fc})</th>
              </tr>
            </thead>
            <tbody>
              {upcomingPayments.top10.map((p, i) => (
                <tr key={i}>
                  <td style={{ fontSize: 12, color: 'var(--t1)' }}>{p.game}</td>
                  <td style={{ fontSize: 12, color: 'var(--t2)' }}>{p.type}</td>
                  <td style={{ fontSize: 12, color: 'var(--t3)' }}>{mlSafe(p.month, startDate)}</td>
                  <td style={{ fontSize: 12, textAlign: 'right', color: 'var(--t1)', fontWeight: 500 }}>
                    {fu(p.amount)}
                  </td>
                </tr>
              ))}
              {upcomingPayments.otherTotal > 0 && (
                <tr style={{ borderTop: '1px solid var(--e1)' }}>
                  <td colSpan={3} style={{ fontSize: 11, color: 'var(--t3)', fontStyle: 'italic' }}>
                    Other (remaining items)
                  </td>
                  <td style={{ fontSize: 12, textAlign: 'right', color: 'var(--t3)' }}>
                    {fu(upcomingPayments.otherTotal)}
                  </td>
                </tr>
              )}
              {upcomingPayments.top10.length === 0 && (
                <tr><td colSpan={4} className="nomsg">No scheduled payments in the next 3 months.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
